import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { current } from 'immer'
import { BillItem, BillType } from '../../../all-types'
import { defaultBillType } from './default-bill-data'
import {
  addDataToOfflineDB,
  deleteDataToOfflineDB,
  OfflineStores,
  updateDataToOfflineDB,
} from '../../../offline-db'
import { createNewBill } from '../../../services'

export type OfflineBill = {
  sessionId: string
  bill: BillType
  isUploadedOnline: boolean
  isActive: boolean
  createdTime: string
}

export interface BillingState {
  offlineBills: { [billId: string]: OfflineBill }
  activeBills: { [uniqueTableNo: string]: OfflineBill }
}

const initialState: BillingState = {
  offlineBills: {},
  activeBills: {},
}

const newBillSession: OfflineBill = {
  sessionId: '',
  bill: defaultBillType,
  createdTime: '',
  isUploadedOnline: false,
  isActive: true,
}

export const BillingStateSlice = createSlice({
  name: 'billState',
  initialState,
  reducers: {
    loadActiveBills: (
      state,
      action: PayloadAction<{ [uniqueTableNo: string]: OfflineBill }>
    ) => {
      state.activeBills = action.payload
    },
    createBillSession: (state, action: PayloadAction<string>) => {
      let bSession = { ...newBillSession, sessionId: action.payload, bill: {...newBillSession.bill, info: {
        ...newBillSession.bill.info,
        sessionId: action.payload
      }}};
      state.activeBills[action.payload] = bSession
      addDataToOfflineDB(OfflineStores.ActiveBills, bSession).then((res) => {
        console.log('Bill session created', res)
      })
    },
    removeBillSession: (state, action: PayloadAction<string>) => {
      if (state.activeBills[action.payload]) {
        delete state.activeBills[action.payload]
      }
    },
    restoreBillSession: (
      state,
      action: PayloadAction<{ sessionId: string; bill: BillType }>
    ) => {
      const { sessionId, bill } = action.payload
      state.activeBills[sessionId] = {
        sessionId,
        bill: { ...bill, info: { ...bill.info, sessionId } },
        isUploadedOnline: false,
        isActive: true,
        createdTime: String(new Date().getTime()),
      }
    },
    updateBill: (
      state,
      action: PayloadAction<{ id: string; bill: BillType }>
    ) => {
      const { id, bill } = action.payload
      if (state.activeBills[id]) {
        state.activeBills[id].bill = bill
        updateDataToOfflineDB(
          OfflineStores.ActiveBills,
          id,
          current(state.activeBills[id])
        )
      }
    },
    addItemsTotheBill: (
      state,
      action: PayloadAction<{
        id: string
        billItem: BillItem
        editIndex: number | null
      }>
    ) => {
      const { id, billItem, editIndex } = action.payload
      const activeBill = state.activeBills[action.payload.id]
      if (activeBill) {
        if (editIndex !== null) {
          activeBill.bill.items[editIndex - 1] = {
            ...billItem,
            sno: editIndex - 1,
          }
        } else {
          const itemsCount = activeBill.bill.items?.length
          activeBill.bill.items.push({ ...billItem, sno: itemsCount })
        }
        updateDataToOfflineDB(
          OfflineStores.ActiveBills,
          id,
          current(activeBill)
        )
      }
    },
    deleteTtemIntheBill: (
      state,
      action: PayloadAction<{ id: string; index: number }>
    ) => {
      const { id, index } = action.payload
      const activeBill = state.activeBills[action.payload.id]
      if (activeBill) {
        activeBill.bill.items.splice(index, 1)
        updateDataToOfflineDB(
          OfflineStores.ActiveBills,
          id,
          current(activeBill)
        )
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        createNewBill.fulfilled,
        (state, action: PayloadAction<BillType>) => {
          const bill: BillType = action.payload
          delete state.activeBills[bill.info.sessionId]
          deleteDataToOfflineDB(
            OfflineStores.ActiveBills,
            bill.info.sessionId
          ).then((res) => {
            console.log('INFO: Bill deleted in active list')
          });
        }
      )
      .addCase(createNewBill.rejected, (state, action) => {
        console.log('add in ofline DB ')
        const bill: BillType = action.payload as BillType
        const bSession = {
          sessionId: bill.billNo,
          bill: bill,
          createdTime: String(new Date().getTime()),
          isUploadedOnline: false,
          isActive: false,
        }
        addDataToOfflineDB(OfflineStores.Bills, bSession).then((res) => {
          console.log('INFO: Bill added to offline bill list')
        })
      })
  },
})

export const {
  loadActiveBills,
  createBillSession,
  removeBillSession,
  restoreBillSession,
  updateBill,
  addItemsTotheBill,
  deleteTtemIntheBill,
} = BillingStateSlice.actions
